import './ReviewView.scss'
import React, { useState, useEffect, useReducer } from 'react'
import { useTranslation } from 'i18n'
import * as Yup from 'yup'
import { Modal, Checkbox } from '@mui/material'
import { Formik, Field } from 'formik'
import Uploads from './Uploads'

import {
  AddressModal,
  NameFields,
  AddressFields,
  returnDynamicValidationUGC,
  validationSchemaUpload,
  renderTextInput,
  FormikRadioGroup2,
} from 'components/forms'

import { addressLookup, addressValidate } from 'services/address'
import { isObjectKeysNotEmpty } from 'util/objectNotEmpty'

const rewardReducer = (state, action) => {
  switch (action.type) {
    case 'changeReward':
      console.log(
        'changeReward action fired. the new reward is: ',
        action.payload,
      )
      const dynamicValidation = () => {
        const {
          nameRequired,
          addressRequired,
          phoneRequired,
          customConfirmation,
          customCheckboxes,
        } = state
        let customConfirmationExists =
          customConfirmation && customConfirmation.length > 0 ? true : false
        let smartValidation = returnDynamicValidationUGC(
          validationSchemaUpload,
          nameRequired,
          addressRequired,
          phoneRequired,
          customConfirmationExists,
          customCheckboxes,
        )
        return Yup.object().shape(smartValidation)
      }
      let reward = action.payload
      let confirmationCopy = reward.confirmationCopy
        ? reward.confirmationCopy
        : ''
      let theNewState = {
        nameRequired: reward.requireName,
        phoneRequired: reward.requirePhoneNumber,
        addressRequired: reward.requireAddress,
        customConfirmation: confirmationCopy,
        customCheckboxes: state.customCheckboxes,
      }
      let smartValidation = dynamicValidation(theNewState)
      return {
        ...theNewState,
        validationSchema: smartValidation,
      }
    default:
      return state
  }
}

const SubmissionForm = ({
  customCheckboxes,
  hasAddressValidation,
  uploadSuccessful,
  rewardOptions = [],
  reviewListHeader,
  instructions = [],
  onFormSubmission,
  nonFieldErrors = [],
  rewardTextPrompt,
  submissionFormTitleLabel,
  submissionFormCaptionLabel,
}) => {
  const { t } = useTranslation(['ugcMediaUpload', 'common'])
  const [addressSuggestionModal, setAddressSuggestionModal] = useState(false)
  const [suggestedAddress, setSuggestionAddress] = useState({})
  const [invalidAddress, setInvalidAddress] = useState(false)
  const customCheckboxesSafe = customCheckboxes ? customCheckboxes : []

  const checkboxKeys = customCheckboxes => {
    let keys = []
    let initialObj = {}
    customCheckboxes.forEach(checkbox => {
      let checkboxKey = `custom-checkbox-${checkbox.id}`
      keys.push(checkboxKey)
      initialObj[checkboxKey] = false
    })
    return [keys, initialObj]
  }
  let [checkboxKeysArray, initialCheckboxObj] =
    checkboxKeys(customCheckboxesSafe)
  //initialize form state with reducer
  let initialState = {
    nameRequired: false,
    phoneRequired: false,
    addressRequired: false,
    customConfirmation: '',
    validationSchema: Yup.object().shape(validationSchemaUpload),
    customCheckboxes: initialCheckboxObj,
  }
  const [state, dispatch] = useReducer(rewardReducer, initialState)

  //onload, pull initial values to make sure that the first reward is selected
  useEffect(() => {
    dispatch({ type: 'changeReward', payload: rewardOptions[0] })
  }, [])

  // reshape the form options beaker spits out and make 'em work for us
  const formRewardOptions = rewardOptions.map(option => ({
    label: option.title,
    value: option.id,
    key: option.id,
  }))

  const initialValues = {
    email: '',
    phoneNumber: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    firstName: '',
    lastName: '',
    customConfirmation: '',
    title: '',
    ...initialCheckboxObj,
  }

  if (rewardOptions && rewardOptions.length > 0) {
    initialValues.rewardId = formRewardOptions[0].value
  }
  const changeFormFieldVisibility = e => {
    let rewardOption = rewardOptions.find(
      option => option.id === e.target.value,
    )
    dispatch({ type: 'changeReward', payload: rewardOption })
  }

  const handleAddressSuggestionModal = () => {
    setAddressSuggestionModal(!addressSuggestionModal)
  }

  const lookupAddress = async values => {
    if (!hasAddressValidation) return
    let addressValues = {
      address1: values.address1 || '',
      address2: values.address2,
      city: values.city,
      state: values.state,
      zip: values.postalCode,
      zip2: '',
    }

    const isAddressNotEmpty = isObjectKeysNotEmpty({
      address1: values.address1,
      city: values.city,
      state: values.state,
      zip: values.postalCode,
    })
    if (isAddressNotEmpty) {
      await addressLookup(addressValues)
        .then(res => {
          const addressSuggestion = res.detail
          setSuggestionAddress(addressSuggestion)
        })
        .then(data => {
          setAddressSuggestionModal(!addressSuggestionModal)
          console.log('data from USPS: ', data)
        })
        .catch(err => console.log(err))
    }
  }

  const validateAddress = async values => {
    setInvalidAddress(false)
    let addressValues = {
      address1: values.address1 || '',
      address2: values.address2,
      city: values.city,
      state: values.state,
      zip: values.postalCode,
      zip2: '',
    }

    const isAddressNotEmpty = isObjectKeysNotEmpty({
      address1: values.address1,
      city: values.city,
      state: values.state,
      zip: values.postalCode,
    })

    if (isAddressNotEmpty) {
      try {
        const res = await addressValidate(addressValues)
        const { openSuggestionModal, suggestionAddress } = res
        if (openSuggestionModal) {
          setSuggestionAddress(suggestionAddress)
          setAddressSuggestionModal(!addressSuggestionModal)
          return false
        }
        return true
      } catch (err) {
        setInvalidAddress(true)
        setAddressSuggestionModal(!addressSuggestionModal)
        return false
      }
    }
    return true
  }

  return (
    <Formik
      validateOnBlur={false}
      validateOnChange={false}
      initialValues={initialValues}
      validationSchema={state.validationSchema}
      onSubmit={async values => {
        values.category = values.rewardId

        if (hasAddressValidation) {
          const isAddressValid = await validateAddress(values)
          if (!isAddressValid) return
          onFormSubmission(values)
        } else if (state.addressRequired) {
          const isAddressValid = await validateAddress(values)
          if (!isAddressValid) return
          onFormSubmission(values)
        } else {
          onFormSubmission(values)
        }
      }}
    >
      {props => {
        const {
          values,
          handleSubmit,
          setValues,
          setFieldValue,
          errors,
          touched,
        } = props

        const handleAddressSuggestionModalAccepted = suggestedAddress => {
          setAddressSuggestionModal(false)
          setValues({
            ...values,
            address1: suggestedAddress.address1 ?? values.address1 ?? '',
            address2: suggestedAddress.address2 ?? values.address2 ?? '',
            city: suggestedAddress.city ?? values.city ?? '',
            state: suggestedAddress.state ?? values.state ?? '',
            postalCode: suggestedAddress.zip ?? values.postalCode ?? '',
          })
        }

        return (
          <form
            className={`receipt-review-form`}
            onSubmit={handleSubmit}
            key={`formik-review-form`}
            autoComplete="on"
          >
            <ul className={'form__elements'} key={`formik-field-elements`}>
              {rewardOptions.length > 1 && (
                <li className="form__element" key={`formik-field-reward`}>
                  <div className="receipt-reward-options">
                    <span className="field-title">{rewardTextPrompt}*</span>
                    <FormikRadioGroup2
                      name="rewardId"
                      setFieldValue={setFieldValue}
                      options={formRewardOptions}
                      value={values.rewardId}
                      defaultValue={formRewardOptions[0].value}
                      onChange={changeFormFieldVisibility}
                    />
                    {errors.rewardId && (
                      <p className="error">{errors.rewardId}</p>
                    )}
                  </div>
                </li>
              )}

              {(state.addressRequired || hasAddressValidation) && (
                <Modal
                  open={addressSuggestionModal}
                  onClose={handleAddressSuggestionModal}
                >
                  <div
                    style={{
                      backgroundColor: 'white',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    <AddressModal
                      notValid={invalidAddress}
                      values={values}
                      onClose={handleAddressSuggestionModal}
                      onAccept={handleAddressSuggestionModalAccepted}
                      suggestedAddress={suggestedAddress}
                    />
                  </div>
                </Modal>
              )}

              <li
                className="form__element form__element-title"
                key={`formik-field-title`}
              >
                <Field
                  name={`title`}
                  type="text"
                  component={renderTextInput}
                  variant="filled"
                  label={`${submissionFormTitleLabel}*`}
                  placeholder={`${submissionFormCaptionLabel}`}
                />
                {errors.title && <p className="error">{errors.title}</p>}
              </li>

              <li
                className="form__element form__element-caption"
                key={`formik-field-1`}
              >
                <Field
                  name={`caption`}
                  id={`caption`}
                  type="textarea"
                  multiline={true}
                  rows={3}
                  inputProps={{ maxLength: 325 }}
                  component={renderTextInput}
                  variant="filled"
                  label={`${submissionFormCaptionLabel}*`}
                  placeholder={`${submissionFormCaptionLabel}`}
                />
              </li>

              {<NameFields {...props} />}

              <li
                className="form__element form__element-email"
                key={`formik-field-2`}
              >
                <Field
                  name={`email`}
                  type="email"
                  component={renderTextInput}
                  variant="filled"
                  label={t('ugcMediaUpload:form.emailLabel')}
                  autoComplete={'on'}
                  placeholder={t('ugcMediaUpload:form.emailPlaceholder')}
                />
                {errors.email && <p className="error">{errors.email}</p>}
              </li>

              <li className="form__element" key={`phoneNumber`}>
                <div className="input-row">
                  <Field
                    name={`phoneNumber`}
                    id={`phoneNumber`}
                    type="phone"
                    component={renderTextInput}
                    variant="filled"
                    label={t('ugcMediaUpload:form.phoneLabel')}
                    placeholder={t('ugcMediaUpload:form.phonePlaceholder')}
                  />
                </div>
                <div className="input-row">
                  {errors.phoneNumber && (
                    <p className="error">{errors.phoneNumber}</p>
                  )}
                </div>
              </li>

              {(state.addressRequired || hasAddressValidation) && (
                <AddressFields lookupAddress={lookupAddress} {...props} />
              )}

              <li className="form__element" key={`formik-field-confirm-items`}>
                <div className={'confirm-items-dialogue'}>
                  {reviewListHeader && <h3>{reviewListHeader}</h3>}
                  <ul className="receipt-review-checkoff">
                    {instructions.map((instruction, index) => {
                      return (
                        <li key={`form-checkoff-item-${index}`}>
                          {/*} <Check />*/}
                          <div className="receipt-review-checkoff-inside">
                            <span
                              dangerouslySetInnerHTML={{
                                __html: instruction.desc,
                              }}
                            ></span>
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              </li>

              {customCheckboxes && customCheckboxes.length > 0 && (
                <>
                  {customCheckboxes.map((checkbox, index) => {
                    let checkboxKey = `custom-checkbox-${checkbox.id}`
                    return (
                      <li
                        className="form__element checkbox-row"
                        key={`formik-field-${checkboxKey}`}
                      >
                        <div className="input-row">
                          <Field
                            type="checkbox"
                            value={values[checkboxKey]}
                            component={Checkbox}
                            name={checkboxKey}
                            touched={touched[checkboxKey] && true ? 'true' : ''}
                            onChange={e => {
                              setFieldValue(checkboxKey, e.target.checked)
                            }}
                          />
                          <label
                            htmlFor={`customConfirmation`}
                            dangerouslySetInnerHTML={{
                              __html: '*' + checkbox.desc,
                            }}
                          ></label>
                        </div>
                        <div className="input-row">
                          {errors[checkboxKey] && (
                            <p className="error">{errors[checkboxKey]}</p>
                          )}
                        </div>
                      </li>
                    )
                  })}
                </>
              )}

              {state.customConfirmation && (
                <li
                  className="form__element checkbox-row"
                  key={`formik-field-customConfirmation`}
                >
                  <div className="input-row">
                    <Field
                      type="checkbox"
                      value={values.customConfirmation}
                      component={Checkbox}
                      name="customConfirmation"
                      touched={touched.customConfirmation && true ? 'true' : ''}
                      onChange={e => {
                        setFieldValue(`customConfirmation`, e.target.checked)
                      }}
                    />

                    <label
                      htmlFor={`customConfirmation`}
                      dangerouslySetInnerHTML={{
                        __html: '*' + state.customConfirmation,
                      }}
                    ></label>
                  </div>

                  <div className="input-row">
                    {errors.customConfirmation && (
                      <p className="error">{errors.customConfirmation}</p>
                    )}
                  </div>
                </li>
              )}

              {nonFieldErrors.length > 0 && (
                <li
                  className="form__submit_errors"
                  key={`formik-submit-errors`}
                >
                  {nonFieldErrors.map(error => {
                    return <p className="error">{error}</p>
                  })}
                </li>
              )}
              {invalidAddress && (
                <li className="form__usps_errors" key={`formik-usps-errors`}>
                  <p className="error">
                    {t('ugcMediaUpload:form.invalidMailingAddress')}
                  </p>
                </li>
              )}
              <li className="form__submit-wrapper" key={`formik-field-cta`}>
                <div className={`receipt-button-container`}>
                  <input
                    disabled={uploadSuccessful === false}
                    className="btn themed-button"
                    type="submit"
                    value={t('common:submit')}
                  />
                </div>
              </li>
            </ul>
          </form>
        )
      }}
    </Formik>
  )
}

const ReviewView = ({
  customCheckboxes,
  hasAddressValidation,
  submissionFormTitleLabel,
  submissionFormCaptionLabel,
  children,
  headline,
  introCopy,
  handleFileSelection,
  onUploadError,
  onUploadSuccessful,
  files,
  onRemove,
  uploadSuccessful,
  uploadID,
  onFormSubmission,
  rewardOptions,
  editMySubmission,
  rewardTextPrompt,
}) => {
  return (
    <div className="receipt-view review-view">
      <div className={`receipt-view-header`}>
        {headline && (
          <h2
            className="receipt-header-headline step-content__title"
            dangerouslySetInnerHTML={{ __html: headline }}
          />
        )}
        {introCopy && (
          <div
            className="receipt-header-desc"
            dangerouslySetInnerHTML={{ __html: introCopy }}
          />
        )}
      </div>
      <Uploads
        handleFileSelection={handleFileSelection}
        onError={onUploadError}
        onSuccess={onUploadSuccessful}
        files={files}
        onRemove={onRemove}
      />

      <div className={`receipt-view-content`}>
        {children}
        <SubmissionForm
          onFormSubmission={onFormSubmission}
          customCheckboxes={customCheckboxes}
          hasAddressValidation={hasAddressValidation}
          submissionFormTitleLabel={submissionFormTitleLabel}
          submissionFormCaptionLabel={submissionFormCaptionLabel}
          rewardTextPrompt={rewardTextPrompt}
          rewardOptions={rewardOptions}
          uploadSuccessful={uploadSuccessful}
          editMySubmission={editMySubmission}
          uploadID={uploadID}
        />
      </div>
    </div>
  )
}
export { ReviewView, SubmissionForm }
